import _ from 'lodash';
import { sqSummaryReportStore } from '@/core/core.stores';
import {
  extractErrorMessage,
  fetchSpcChartTable,
  fetchStatsSummaryData,
} from '@/summaryReport/summaryReport.utilities';
import { ALIGN_WEEKLY_ID } from '@/tools/statisticsSummary/statisticsSummary.constants';
import { findItem } from '@/trendData/itemStore.utilities';
import { setTrendItemColor } from '@/trendData/trend.actions';
import { ITEM_DATA_STATUS } from '@/trendData/trendData.constants';
import { errorToast } from '@/utilities/toast.utilities';
import { sqFormulasApi } from '@seeqdev/api/endpoint/FormulasApi';
import type { GenericTableOutput } from 'generated-plugin-api/seeq';
import type { StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import type { ItemSearchPreviewV1 } from '@seeqdev/api/model/ItemSearchPreviewV1';

type SummaryReportItemPreview = ItemSearchPreviewV1 & { color?: string };

export interface ItemSignalAndCondition {
  signal?: SummaryReportItemPreview;
  condition?: SummaryReportItemPreview;
  alignmentCondition?: SummaryReportItemPreview & {
    conditionMetadata?: { maximumDuration?: { value?: number | null; uom?: string | null } };
  };
  formula?: string;
}

async function fetchAndBuildItemPayload(item: StatisticsSummaryItem): Promise<StatisticsSummaryItem> {
  const result = await fetchStatsSummaryData(item.id);
  const { signal, condition, alignmentCondition, formula } = await fetchItemSignalAndCondition(item.id);

  const hasXbarSpcChart = !!(result as StatisticsSummaryItem)?.data?.spcCharts?.['XBAR'];
  const alignmentConditionId =
    hasXbarSpcChart && !alignmentCondition?.id ? ALIGN_WEEKLY_ID : (alignmentCondition?.id ?? '');

  let spcChartTable;
  let warnings;
  if (signal?.id) {
    const chartTableResponse = await fetchSpcChartTable(
      item.id,
      signal.id,
      condition?.id,
      alignmentConditionId,
      alignmentCondition?.conditionMetadata,
      formula,
    );
    spcChartTable = chartTableResponse.table;
    if (chartTableResponse.warningLogs && chartTableResponse.warningLogs.length > 0) {
      const warningMessages = new Set<string>();
      chartTableResponse.warningLogs.forEach((log) => {
        if (log.formulaLogEntries) {
          Object.values(log.formulaLogEntries).forEach((entry) => {
            if (entry.logDetails) {
              entry.logDetails.forEach((detail: any) => {
                if (detail.message) {
                  // Add period if not present, deduplicate messages
                  const message = detail.message.trim();
                  const messageWithPeriod = message.endsWith('.') ? message : `${message}.`;
                  warningMessages.add(messageWithPeriod);
                }
              });
            }
          });
        }
      });
      if (warningMessages.size > 0) {
        warnings = Array.from(warningMessages).map((message) => ({ message }));
      }
    }
  }

  return { ...item, ...result, tableData: spcChartTable as GenericTableOutput, warnings, error: undefined };
}

export async function addStatsSummary(item: StatisticsSummaryItem): Promise<void> {
  await fetchAndBuildItemPayload(item)
    .then((payload) => {
      sqSummaryReportStore.actions.addItem(payload);
      setTrendItemColor(payload.id, payload.color);
    })
    .catch((error) => {
      const errorMessage = extractErrorMessage(error);
      sqSummaryReportStore.actions.addItem({ ...item, error: { message: errorMessage } });
    })
    .finally(() => {
      sqSummaryReportStore.actions.setStatus(ITEM_DATA_STATUS.PRESENT);
    });
}

export async function insertStatsSummaryAtIndex(item: StatisticsSummaryItem, index: number): Promise<void> {
  await fetchAndBuildItemPayload(item)
    .then((payload) => {
      sqSummaryReportStore.actions.insertItemAtIndex(payload, index);
      setTrendItemColor(payload.id, payload.color);
    })
    .catch((error) => {
      const errorMessage = extractErrorMessage(error);
      sqSummaryReportStore.actions.insertItemAtIndex({ ...item, error: { message: errorMessage } }, index);
    })
    .finally(() => {
      sqSummaryReportStore.actions.setStatus(ITEM_DATA_STATUS.PRESENT);
    });
}

export async function refreshStatsData(id: string): Promise<void> {
  const item = findItem(sqSummaryReportStore.getState().items, id) as StatisticsSummaryItem | undefined;
  if (!item) {
    return;
  }
  await fetchAndBuildItemPayload(item)
    .then((payload) => {
      sqSummaryReportStore.actions.updateItem(payload);
      setTrendItemColor(payload.id, payload.color);
    })
    .catch((error) => {
      const errorMessage = extractErrorMessage(error);
      sqSummaryReportStore.actions.updateItem({ ...item, error: { message: errorMessage } });
    })
    .finally(() => {
      sqSummaryReportStore.actions.setStatus(ITEM_DATA_STATUS.PRESENT);
    });
}

export async function fetchItemsSignalAndConditionMap(
  items: StatisticsSummaryItem[],
): Promise<Record<string, ItemSignalAndCondition>> {
  const signalAndConditionMap: Record<string, ItemSignalAndCondition> = {};

  await Promise.all(
    items.map(async (item: StatisticsSummaryItem) => {
      signalAndConditionMap[item.id] = await fetchItemSignalAndCondition(item.id);
    }),
  );

  return signalAndConditionMap;
}

export async function fetchItemSignalAndCondition(itemId: string): Promise<ItemSignalAndCondition> {
  try {
    const { data } = await sqFormulasApi.getItem({ id: itemId });
    const signal = _.find(data.parameters, (param) => param.name === 'signal')?.item as SummaryReportItemPreview;
    const condition = _.find(data.parameters, (param) => param.name === 'condition')?.item as SummaryReportItemPreview;
    const alignmentCondition = _.find(data.parameters, (param) => param.name === 'alignmentCondition')
      ?.item as SummaryReportItemPreview;
    return { signal, condition, alignmentCondition, formula: data.formula };
  } catch (error) {
    // TODO CRAB-53442 - Add improved and consistent error and redaction handling
    errorToast({ httpResponseOrError: error });
    return { signal: undefined, condition: undefined };
  }
}

export async function fetchStatSummaries(): Promise<void> {
  const { items } = sqSummaryReportStore.getState();
  if (!items?.length) return;

  sqSummaryReportStore.actions.setStatus(ITEM_DATA_STATUS.LOADING);
  try {
    const updatedItems = await Promise.all(
      _.map(items, (item) =>
        fetchAndBuildItemPayload(item).catch((error) => {
          const errorMessage = extractErrorMessage(error);
          return { ...item, error: { message: errorMessage } };
        }),
      ),
    );
    sqSummaryReportStore.actions.setItems(updatedItems);
  } finally {
    sqSummaryReportStore.actions.setStatus(ITEM_DATA_STATUS.PRESENT);
  }
}
